import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { resolveApiMessage, showToast } from '../../../helpers';
import { getOpsCatalogue, updateOpsProduct } from '../../../requests';
import { TOpsProduct } from '../../../types';
import {
  btnPrimary,
  btnSecondary,
  Card,
  EmptyState,
  inputClass,
  labelClass,
  OriginBadge,
  PageBody,
  PageHeader,
  Pagination,
  rowClickClass,
  tableClass,
  tdClass,
  thClass,
  theadClass,
  Toolbar,
} from '../ui';

const FAMILIES = [
  'Business Application',
  'Professional Development',
  'Advanced Business Application',
  '3rd Party',
  'Customer Specific Workshops',
  'Services',
];

const OpsCatalogue: React.FC = () => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [family, setFamily] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<TOpsProduct | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useQuery({
    queryKey: ['opsCatalogue', q, family, activeOnly, page],
    queryFn: () =>
      getOpsCatalogue({
        q: q || undefined,
        family: family || undefined,
        active: activeOnly,
        page,
      }),
  });

  const save = useMutation({
    mutationFn: (p: TOpsProduct) =>
      updateOpsProduct(p.id, {
        name: p.name ?? '',
        family: p.family ?? undefined,
        code: p.code ?? undefined,
        active: p.active,
        websiteReady: p.websiteReady,
        description: p.description ?? undefined,
        listPrice: p.listPrice,
      }),
    onSuccess: () => {
      showToast('Product saved', 'success');
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['opsCatalogue'] });
    },
    onError: (err) => showToast(resolveApiMessage(err), 'error'),
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Course catalogue"
        meta={data && `${data.total.toLocaleString()} products`}
      />
      <Toolbar>
        <input
          className={`${inputClass} w-64`}
          placeholder="Search products…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={inputClass}
          value={family}
          onChange={(e) => {
            setFamily(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All families</option>
          {FAMILIES.map((f) => (
            <option key={f}>{f}</option>
          ))}
        </select>
        <label className="flex items-center gap-1.5 text-sm text-gray-three">
          <input
            type="checkbox"
            className="ops-check"
            checked={activeOnly}
            onChange={(e) => {
              setActiveOnly(e.target.checked);
              setPage(1);
            }}
          />
          Active only
        </label>
      </Toolbar>

      {editing && (
        <div className="px-8 pb-4">
          <Card title={`Edit — ${editing.name ?? 'product'}`}>
            <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2">
              <div className="md:col-span-2">
                <div className={labelClass}>Product name</div>
                <input
                  className={`${inputClass} w-full`}
                  value={editing.name ?? ''}
                  onChange={(e) =>
                    setEditing({ ...editing, name: e.target.value })
                  }
                />
              </div>
              <div>
                <div className={labelClass}>Family</div>
                <select
                  className={`${inputClass} w-full`}
                  value={editing.family ?? ''}
                  onChange={(e) =>
                    setEditing({ ...editing, family: e.target.value })
                  }
                >
                  <option value="">—</option>
                  {FAMILIES.map((f) => (
                    <option key={f}>{f}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className={labelClass}>Code</div>
                <input
                  className={`${inputClass} w-full`}
                  value={editing.code ?? ''}
                  onChange={(e) =>
                    setEditing({ ...editing, code: e.target.value })
                  }
                />
              </div>
              <div>
                <div className={labelClass}>List price (USD)</div>
                <input
                  className={`${inputClass} w-full`}
                  type="number"
                  min={0}
                  step="0.01"
                  placeholder={
                    editing.bookPrice != null
                      ? `${editing.bookPrice} from the Salesforce price book`
                      : 'No price — hidden from learners'
                  }
                  value={editing.listPrice ?? ''}
                  onChange={(e) =>
                    setEditing({
                      ...editing,
                      listPrice:
                        e.target.value === '' ? null : Number(e.target.value),
                    })
                  }
                />
                <div className="mt-1 text-xs text-gray-five">
                  {editing.listPrice != null
                    ? editing.bookPrice != null
                      ? `Overrides the price book (${editing.bookPrice}).`
                      : 'The portal’s price for this product.'
                    : editing.bookPrice != null
                    ? 'Blank = the Salesforce price book applies.'
                    : 'Without a price the product never appears in the learner catalogue.'}
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-five">
                <input
                  type="checkbox"
                  className="ops-check"
                  checked={editing.active}
                  onChange={(e) =>
                    setEditing({ ...editing, active: e.target.checked })
                  }
                />
                Active
              </label>
              <label className="flex items-center gap-2 text-sm text-gray-five">
                <input
                  type="checkbox"
                  className="ops-check"
                  checked={editing.websiteReady}
                  onChange={(e) =>
                    setEditing({ ...editing, websiteReady: e.target.checked })
                  }
                />
                Website ready
              </label>
              <div className="flex gap-3 md:col-span-2">
                <button
                  className={btnPrimary}
                  disabled={save.isPending || !(editing.name ?? '').trim()}
                  onClick={() => save.mutate(editing)}
                >
                  {save.isPending ? 'Saving…' : 'Save'}
                </button>
                <button
                  className={btnSecondary}
                  onClick={() => setEditing(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <PageBody wide>
          <Card>
            {data && data.items.length === 0 ? (
              <EmptyState
                title="No products match"
                hint="Try clearing the search, or untick “Active only”."
              />
            ) : (
              <div className="overflow-x-auto">
                <table className={`${tableClass} min-w-[900px]`}>
                  <thead className={theadClass}>
                    <tr>
                      <th className={thClass}>Product</th>
                      <th className={thClass}>Family</th>
                      <th className={thClass}>Code</th>
                      <th className={`${thClass} text-right`}>Classes</th>
                      <th className={`${thClass} text-right`}>Price</th>
                      <th className={thClass}>Active</th>
                      <th className={thClass}>Website</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data?.items.map((p) => (
                      <tr
                        key={p.id}
                        className={rowClickClass}
                        onClick={() => setEditing(p)}
                      >
                        <td className={`${tdClass} font-medium text-gray-four`}>
                          {p.name}
                          {p.localOrigin && (
                            <span className="ml-2">
                              <OriginBadge local />
                            </span>
                          )}
                        </td>
                        <td className={tdClass}>{p.family ?? '—'}</td>
                        <td className={tdClass}>{p.code ?? '—'}</td>
                        <td className={`${tdClass} text-right`}>
                          {p.classCount}
                        </td>
                        <td
                          className={`${tdClass} text-right`}
                          title={
                            p.listPrice != null
                              ? 'Set on the portal'
                              : p.bookPrice != null
                              ? 'Salesforce price book'
                              : 'No price — not in the learner catalogue'
                          }
                        >
                          {p.price != null ? `$${p.price}` : '—'}
                          {p.listPrice != null && (
                            <span className="ml-1 text-xs text-gray-five">
                              portal
                            </span>
                          )}
                        </td>
                        <td className={tdClass}>{p.active ? '✓' : '—'}</td>
                        <td className={tdClass}>
                          {p.websiteReady ? '✓' : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <Pagination
              page={page}
              pages={pages}
              total={data?.total}
              onPage={setPage}
            />
          </Card>
        </PageBody>
      )}
    </>
  );
};

export default OpsCatalogue;
